import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, api, type ConsoleSession } from "../api";

/* The console handoff.
 *
 * A console session is single-use and expires in about a minute, and this
 * component says so out loud rather than hiding it. That is not decoration:
 * the short window is the reason a console URL is safe to hand to a browser
 * at all, and showing the clock draining teaches the person using it why
 * they cannot bookmark the link.
 */

type Phase = "idle" | "requesting" | "ready" | "connecting" | "open" | "closed" | "error";

export function ConsolePanel({ serviceId, name }: { serviceId: string; name: string }) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [session, setSession] = useState<ConsoleSession | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<{ disconnect(): void } | null>(null);

  /* Count the pickup window down. When it reaches zero the session is already
     dead server-side, so the UI must not offer a Connect button that would
     fail — it offers a new session instead. */
  useEffect(() => {
    if (phase !== "ready" || !session) return;
    setRemaining(session.ttl_seconds);
    const timer = setInterval(() => {
      setRemaining((value) => {
        if (value <= 1) {
          clearInterval(timer);
          setPhase("idle");
          setSession(null);
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [phase, session]);

  useEffect(() => () => rfbRef.current?.disconnect(), []);

  const request = useCallback(async () => {
    setPhase("requesting");
    setError(null);
    try {
      const created = await api.post<ConsoleSession>(`/api/services/${serviceId}/console`, {
        kind: "vnc",
      });
      setSession(created);
      setPhase("ready");
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "The console could not be opened",
      );
      setPhase("error");
    }
  }, [serviceId]);

  /* The stage element only exists once the phase is "connecting", so the
     click merely changes phase and noVNC attaches here, after that render.
     Attaching inside the click handler found no stage and did nothing. */
  const connect = useCallback(() => {
    if (session) setPhase("connecting");
  }, [session]);

  useEffect(() => {
    if (phase !== "connecting" || !session) return;
    let cancelled = false;
    (async () => {
      try {
        const { default: RFB } = await import("@novnc/novnc");
        // Unmounted, or no longer connecting, while noVNC was loading: attach nothing.
        if (cancelled || !stageRef.current) return;
        const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${session.websocket_path}`;
        const rfb = new RFB(stageRef.current, url, { wsProtocols: ["binary"] });
        rfb.scaleViewport = true;
        rfb.clipViewport = true;
        rfb.addEventListener("connect", () => setPhase("open"));
        rfb.addEventListener("disconnect", () => {
          rfbRef.current = null;
          setPhase("closed");
        });
        rfbRef.current = rfb;
      } catch {
        if (cancelled) return;
        setError("The console connection failed. Request a new session and try again.");
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, session]);

  function disconnect() {
    rfbRef.current?.disconnect();
    rfbRef.current = null;
    setPhase("closed");
    setSession(null);
  }

  if (phase === "connecting" || phase === "open" || phase === "closed") {
    return (
      <section>
        <div className="console-bar">
          <strong style={{ color: "var(--ink)" }}>{name}</strong>
          <span>{phase === "open" ? "connected" : phase === "connecting" ? "connecting…" : "session ended"}</span>
          <button
            type="button"
            className="btn"
            style={{ marginLeft: "auto" }}
            onClick={phase === "closed" ? request : disconnect}
          >
            {phase === "closed" ? "Start a new session" : "Disconnect"}
          </button>
        </div>
        <div className="console-stage" ref={stageRef} />
      </section>
    );
  }

  return (
    <section className="console-gate">
      <h2 style={{ marginBottom: "0.15rem" }}>Console</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: "var(--step--1)" }}>
        A direct screen and keyboard connection, the same as standing at the machine. Useful
        when the network is down or the operating system will not boot.
      </p>

      {error && (
        <p className="notice notice--error" role="alert" style={{ marginTop: "1rem" }}>
          {error}
        </p>
      )}

      {phase !== "ready" ? (
        <button
          type="button"
          className="btn btn--primary"
          style={{ marginTop: "1.25rem" }}
          disabled={phase === "requesting"}
          onClick={request}
        >
          {phase === "requesting" ? "Opening…" : "Open console"}
        </button>
      ) : (
        <div className={`countdown${remaining <= 15 ? " countdown--expiring" : ""}`}>
          <p className="label">Session expires in</p>
          <p className="countdown__figure">
            {String(Math.floor(remaining / 60)).padStart(2, "0")}:
            {String(remaining % 60).padStart(2, "0")}
          </p>
          <div className="countdown__bar">
            <i style={{ width: `${(remaining / (session?.ttl_seconds || 60)) * 100}%` }} />
          </div>
          <p className="muted" style={{ fontSize: "var(--step--1)", marginTop: "0.8rem" }}>
            This link works once and only from this browser. It cannot be shared or reused.
          </p>
          <button type="button" className="btn btn--primary" onClick={connect}>
            Connect now
          </button>
        </div>
      )}
    </section>
  );
}
